import { Role } from '@robu/shared';
import type { ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';

import { FullPageLoader } from '../../components/layout/RouteFallback';
import { EmptyState } from '../../components/ui/EmptyState';
import { useAuth } from './useAuth';

/**
 * Route guards are a UX affordance only. Authorisation is enforced by the API on
 * every request (TZ §36); hiding a link never replaces a server-side check.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader label="Проверяем сессию…" />;

  if (status === 'anonymous') {
    // Remember where the user was heading so login can return them there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children ?? <Outlet />}</>;
}

export function RequireRole({ roles, children }: { roles: readonly Role[]; children?: ReactNode }) {
  const { status, user } = useAuth();

  if (status === 'loading') return <FullPageLoader label="Проверяем доступ…" />;
  if (status === 'anonymous' || !user) return <Navigate to="/login" replace />;

  if (!roles.includes(user.role)) {
    return (
      <EmptyState
        tone="negative"
        icon="⛔"
        title="Недостаточно прав"
        description="Этот раздел доступен только пользователям с расширенными правами."
        action={
          <Link
            to="/"
            className="inline-flex min-h-11 items-center rounded-control border border-line-strong px-4 text-sm font-semibold text-ink hover:bg-surface-raised"
          >
            На главную
          </Link>
        }
      />
    );
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * The player area is for players only. Staff accounts have no profile, balance
 * or predictions, so they are sent to the admin panel instead; match and round
 * pages, which staff reach through shared links, have admin-panel twins.
 */
export function RequirePlayer({ children }: { children?: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user && user.role !== Role.PLAYER) {
    const shared = /^\/(matches|rounds)\/[^/]+$/.test(location.pathname);
    return <Navigate to={shared ? `/admin${location.pathname}` : '/admin'} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

/** Keeps authenticated users out of the login / invitation screens. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === 'loading') return <FullPageLoader label="Проверяем сессию…" />;
  if (status === 'authenticated') return <Navigate to="/" replace />;

  return <>{children}</>;
}
